import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import BottomNav from "../components/BottomNav";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Bell, Heart, MessageCircle, Eye, Star, Check } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { Button } from "../components/ui/button";

import { API } from "../config";
import { tx } from "../lib/tx";

const NOTIF_ICONS = {
  like: Heart,
  super_like: Star,
  match: Heart,
  message: MessageCircle,
  profile_view: Eye
};

const NOTIF_COLORS = {
  like: "text-primary",
  super_like: "text-secondary",
  match: "text-green-500",
  message: "text-blue-500",
  profile_view: "text-purple-500"
};

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 15000);
    return () => clearInterval(iv);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/notifications`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/notifications/read`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Mark read error:", error);
    }
  };

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: fr });
    } catch {
      return "";
    }
  };

  const handleNotificationClick = (notif) => {
    if (notif.type === "message") {
      navigate(`/chat/${notif.from_user_id}`);
    } else if (notif.type === "match") {
      navigate(`/chat/${notif.from_user_id}`);
    } else if (notif.type === "profile_view" || notif.type === "like" || notif.type === "super_like") {
      navigate("/who-liked-me");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="notifications-page">
      
      <div className="pt-6 pb-24 px-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-heading text-3xl font-bold text-white">
              Notifications
            </h1>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllRead}
                className="text-primary"
                data-testid="mark-all-read-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                {tx("Tout marquer lu")}
              </Button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-6">
                <Bell className="w-12 h-12 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl text-white mb-2">{tx("Aucune notification")}</h3>
              <p className="text-muted-foreground">
                {tx("Vos notifications apparaîtront ici")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notif) => {
                const Icon = NOTIF_ICONS[notif.type] || Bell;
                const iconColor = NOTIF_COLORS[notif.type] || "text-muted-foreground";
                
                return (
                  <div
                    key={notif.notification_id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`p-4 rounded-2xl flex items-center gap-4 cursor-pointer transition-all ${
                      notif.read 
                        ? "bg-card/30 border border-white/5" 
                        : "bg-card/50 border border-primary/30"
                    }`}
                    data-testid={`notification-${notif.notification_id}`}
                  >
                    {/* User Avatar */}
                    <div className="relative">
                      <Avatar className="w-14 h-14">
                        <AvatarImage src={notif.from_user?.picture} />
                        <AvatarFallback className="bg-primary text-white">
                          {notif.from_user?.name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-card flex items-center justify-center ${iconColor}`}>
                        <Icon className="w-3.5 h-3.5 fill-current" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`${notif.read ? "text-muted-foreground" : "text-white"}`}>
                        {notif.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTime(notif.created_at)}
                      </p>
                    </div>
                    
                    {/* Unread indicator */}
                    {!notif.read && (
                      <div className="w-3 h-3 rounded-full bg-primary" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
